import { LogManager } from '@src/utility/Logger';

import type { Drawing, Stroke, StrokeTool } from '@src/types.ts/comment';

const logger = LogManager.getLogger('comments:drawing');

/** Current `Drawing.v`. Bump only on a breaking payload change. */
export const DRAWING_PAYLOAD_VERSION = 1;

/**
 * The valid tool names. Typed as `Set<StrokeTool>` on construction so a typo is
 * a compile error, then read as `ReadonlySet<string>` so an arbitrary parsed
 * value can be tested against it without a cast.
 */
const TOOL_NAMES: ReadonlySet<string> = new Set<StrokeTool>([
  'freehand',
  'arrow',
  'line',
  'rect',
]);

/** Shape tools describe a box or a segment, so they carry exactly two points. */
const TWO_POINT_TOOLS: readonly StrokeTool[] = ['arrow', 'line', 'rect'];

const isNormalized = (n: unknown): n is number =>
  typeof n === 'number' && Number.isFinite(n) && n >= 0 && n <= 1;

const isPoint = (value: unknown): value is [number, number] =>
  Array.isArray(value) &&
  value.length === 2 &&
  isNormalized(value[0]) &&
  isNormalized(value[1]);

const isTool = (value: unknown): value is StrokeTool =>
  typeof value === 'string' && TOOL_NAMES.has(value);

/**
 * Narrow an unknown to a plain object so its fields can be read one by one.
 *
 * The only type assertion in this module, and deliberately centralised here:
 * a freshly parsed JSON value is precisely the boundary where the shape is
 * genuinely unknown, and every field is validated immediately afterwards.
 */
function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

/**
 * Validate one stroke, returning it typed or `null`.
 *
 * Rejects rather than repairs. A stroke with a point outside `0–1` is not a
 * stroke we can place on the frame, and guessing where it was meant to go would
 * put a mark somewhere the reviewer never drew — worse than showing nothing.
 */
function readStroke(value: unknown): Stroke | null {
  const raw = asRecord(value);
  if (!raw) return null;

  if (!isTool(raw.tool)) return null;
  if (typeof raw.color !== 'string' || raw.color.length === 0) return null;
  if (typeof raw.width !== 'number' || !Number.isFinite(raw.width)) return null;
  if (raw.width <= 0) return null;
  if (!Array.isArray(raw.points)) return null;
  if (!raw.points.every(isPoint)) return null;

  // A single point cannot describe any of the four marks.
  if (raw.points.length < 2) return null;
  if (TWO_POINT_TOOLS.includes(raw.tool) && raw.points.length !== 2) {
    return null;
  }

  return {
    tool: raw.tool,
    color: raw.color,
    width: raw.width,
    points: raw.points,
  };
}

/**
 * Normalize a stored or incoming `drawing` value into a `Drawing`, or `null`.
 *
 * **Tolerant by design, and never throws.** Two reasons a payload can be
 * imperfect: the field is JSON, so the CDA hands it back already parsed while
 * the CMA hands back a string (the same split `reactions` and `mentions`
 * already absorb); and a future client may write strokes this one does not
 * understand. In both cases a comment must still render — its text is the point,
 * the marks are supporting detail. So unreadable strokes are dropped with a
 * warning and the readable ones are kept.
 *
 * Returns `null` when there is nothing usable, which is also what a comment with
 * no drawing reads back as, so callers have one case to handle rather than two.
 *
 * Used on both sides deliberately: on read to clean what storage returned, and
 * on write to clean what the UI produced, so a bad payload can never be
 * persisted in the first place.
 */
export function sanitizeDrawing(raw: unknown): Drawing | null {
  if (raw == null) return null;

  let value = raw;
  if (typeof raw === 'string') {
    if (raw.trim().length === 0) return null;
    try {
      value = JSON.parse(raw);
    } catch {
      logger.warn('Unparseable drawing value — treating as no drawing');
      return null;
    }
  }

  const payload = asRecord(value);
  if (!payload) {
    if (value != null) {
      logger.warn('Drawing payload is not an object — treating as no drawing');
    }
    return null;
  }

  if (!Array.isArray(payload.strokes)) {
    logger.warn('Drawing payload has no strokes array — treating as none');
    return null;
  }

  const strokes = payload.strokes
    .map(readStroke)
    .filter((stroke): stroke is Stroke => stroke !== null);

  const dropped = payload.strokes.length - strokes.length;
  if (dropped > 0) {
    logger.warn('Dropped unreadable strokes from a drawing', {
      dropped,
      kept: strokes.length,
      // A version we don't recognise is the likeliest cause, so surface it.
      payloadVersion: payload.v,
    });
  }

  // A drawing with nothing left to paint is indistinguishable from no drawing,
  // and storing an empty one would show the "has a drawing" indicator on a
  // comment with nothing to show.
  if (strokes.length === 0) return null;

  return { v: DRAWING_PAYLOAD_VERSION, strokes };
}
